import React, { useState, useEffect } from 'react';
import { Calendar as CalIcon, Clock, ChevronLeft, ChevronRight, Users, Check, X, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_SLOTS = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];

const ScheduleManager = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [requests, setRequests] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tab, setTab] = useState('calendar');

  useEffect(() => { fetchAvailability(); fetchRequests(); }, [currentMonth]);

  const fetchAvailability = async () => {
    const m = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
    try {
      const res = await fetch(`${API_URL}/api/schedule/admin/availability?month=${m}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      const map = {};
      data.forEach(s => { map[s.date] = s; });
      setAvailability(map);
    } catch (e) { console.error(e); }
  };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/schedule/admin/requests`, { headers: { 'Authorization': `Bearer ${token}` } });
      setRequests(await res.json());
    } catch (e) { console.error(e); }
  };

  const toggleDate = async (day) => {
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    const date = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const current = availability[date];
    const newAvail = !current?.available;

    await fetch(`${API_URL}/api/schedule/admin/availability`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ date, available: newAvail, time_slots: current?.time_slots || DEFAULT_SLOTS })
    });
    setAvailability({ ...availability, [date]: { ...current, date, available: newAvail, time_slots: current?.time_slots || DEFAULT_SLOTS } });
    toast({ title: newAvail ? 'Date Opened' : 'Date Blocked', description: date });
  };

  const updateTimeSlots = async (date, slots) => {
    await fetch(`${API_URL}/api/schedule/admin/availability`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ date, available: true, time_slots: slots })
    });
    setAvailability({ ...availability, [date]: { ...availability[date], time_slots: slots } });
  };

  const updateRequest = async (id, status) => {
    await fetch(`${API_URL}/api/schedule/admin/requests/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    toast({ title: 'Updated', description: `Request ${status}` });
    fetchRequests();
  };

  const getDaysInMonth = () => {
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    return { first: new Date(y, m, 1).getDay(), days: new Date(y, m + 1, 0).getDate() };
  };

  const formatDate = (day) => {
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const { first, days } = getDaysInMonth();
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="p-6 space-y-6" data-testid="schedule-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Schedule Manager</h1>
          <p className="text-sm text-[#6C757D]">Manage meeting availability and booking requests</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button variant={tab === 'calendar' ? 'default' : 'outline'} onClick={() => setTab('calendar')} className={tab === 'calendar' ? 'bg-[#023E8A]' : ''}>
          <CalIcon className="w-4 h-4 mr-2" /> Calendar
        </Button>
        <Button variant={tab === 'requests' ? 'default' : 'outline'} onClick={() => setTab('requests')} className={tab === 'requests' ? 'bg-[#023E8A]' : ''}>
          <Users className="w-4 h-4 mr-2" /> Requests {pendingCount > 0 && <Badge className="ml-2 bg-[#FF6B35]">{pendingCount}</Badge>}
        </Button>
      </div>

      {tab === 'calendar' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="bg-[#023E8A] text-white rounded-t-lg">
                <div className="flex items-center justify-between">
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="hover:bg-white/10 p-2 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
                  <CardTitle>{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</CardTitle>
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="hover:bg-white/10 p-2 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-xs text-[#6C757D] mb-3">Click a date to toggle Available (green) / Blocked (gray)</p>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS.map(d => <div key={d} className="text-center text-xs font-bold text-[#6C757D] py-2">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array(first).fill(null).map((_, i) => <div key={`e-${i}`} />)}
                  {Array.from({ length: days }, (_, i) => i + 1).map(day => {
                    const date = formatDate(day);
                    const slot = availability[date];
                    const avail = slot?.available;
                    const isSelected = selectedDate === date;
                    return (
                      <button key={day} onClick={() => { toggleDate(day); setSelectedDate(date); }}
                        className={`aspect-square rounded-lg text-sm font-semibold transition-all border-2
                          ${avail ? 'bg-green-50 border-green-400 text-green-700 hover:bg-green-100' : 'bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100'}
                          ${isSelected ? 'ring-2 ring-[#FF6B35] ring-offset-2' : ''}`}>
                        {day}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-6 mt-4 text-xs text-[#6C757D]">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-50 border-2 border-green-400" /> Available</div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-50 border-2 border-gray-200" /> Blocked</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Time Slots Editor */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Time Slots</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDate && availability[selectedDate]?.available ? (
                  <>
                    <p className="text-sm text-[#6C757D] mb-3">{selectedDate}</p>
                    <div className="space-y-2">
                      {['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'].map(time => {
                        const active = (availability[selectedDate]?.time_slots || []).includes(time);
                        return (
                          <button key={time} onClick={() => {
                            const current = availability[selectedDate]?.time_slots || [];
                            const updated = active ? current.filter(t => t !== time) : [...current, time].sort();
                            updateTimeSlots(selectedDate, updated);
                          }}
                            className={`w-full py-2 rounded-lg text-sm font-medium transition-all ${active ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
                            {time} {active && <Check className="w-3 h-3 inline ml-1" />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-[#6C757D]">Select an available date to manage time slots.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <div className="space-y-4">
          {requests.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-[#6C757D]">No booking requests yet.</CardContent></Card>
          ) : requests.map(req => (
            <Card key={req.id} className={req.status === 'pending' ? 'border-l-4 border-l-[#FF6B35]' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-[#2B2D42]">{req.name}</span>
                      <Badge className={req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : req.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{req.status}</Badge>
                    </div>
                    <div className="text-sm text-[#6C757D] space-y-1">
                      <p><Mail className="w-3 h-3 inline mr-1" />{req.email} {req.company && `| ${req.company}`}</p>
                      {req.subject && <p>Subject: {req.subject}</p>}
                      {req.message && <p className="italic">"{req.message}"</p>}
                    </div>
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-[#2B2D42] mb-1">Preferred Slots:</p>
                      {(req.preferred_slots || []).map((s, i) => (
                        <div key={i} className="text-xs text-[#6C757D] flex items-center gap-2">
                          <CalIcon className="w-3 h-3" /> {s.date} at {(s.time_slots || []).join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                  {req.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600" onClick={() => updateRequest(req.id, 'confirmed')}><Check className="w-3 h-3 mr-1" /> Confirm</Button>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-500" onClick={() => updateRequest(req.id, 'rejected')}><X className="w-3 h-3 mr-1" /> Reject</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;
